import { useEffect, useRef } from 'react';

/**
 * Jumps to the anchor named in the URL on the first render that has content.
 *
 * The browser only honours a fragment against the document it has already
 * painted, so a shared link like /report#headroom lands on a page whose target
 * does not exist yet — the section arrives with the fetched data. This runs the
 * jump once that content is in the DOM. Later, same-page anchor clicks are left
 * to the browser.
 */
export function useHashScroll(ready: boolean) {
  const jumped = useRef(false);

  useEffect(() => {
    if (!ready || jumped.current) return;
    jumped.current = true;

    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;

    // A frame's grace so charts have laid out and the target sits at its final
    // offset; without it the jump lands short.
    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView();
    });
    return () => cancelAnimationFrame(frame);
  }, [ready]);
}
